import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tooltip,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel
} from "@mui/material";
import LOGO_BASE64 from "../../assets/data/logoBase64";
import { FaEdit } from "react-icons/fa";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useAuth } from "../../context/AuthContext";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { FaTrash } from "react-icons/fa";
import ConfirmDialog from "../../components/ConfirmDialog";

// Yup Validation Schema
const schema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters"),
  mobile: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{10}$/, "Enter a valid 10-digit number"),
  location: Yup.string().required("Location is required"),
  branch: Yup.string().required('Please select a branch')
}).required();

function ListPage() {
  const [open, setOpen] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editCustomer, setEditCustomer] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchString, setSearchString] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [branches, setBranches] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [filteredBranch, setFilteredBranch] = useState('');
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [openTrayDialog, setOpenTrayDialog] = useState(false);
  const [trayTypes, setTrayTypes] = useState([]);
  const [selectedCustomerTrayId, setSelectedCustomerTrayId] = useState("");
  const [trayTypeId, setTrayTypeId] = useState("");
  const [trayCount, setTrayCount] = useState("");
  const [savingTray, setSavingTray] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const navigate = useNavigate();

  const { user } = useAuth();

  // Fetch Customers on Mount
  useEffect(() => {
    // getCustomersList();
    getBranchList()
    if(user?.roleId === 1){
      getEmployersList()
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0)
      getCustomersList();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchString, filteredBranch]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0)
      getBranchList()
      getTrayTypes();
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedEmployerId]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const getTrayTypes = async () => {
    try {
      if(!selectedEmployerId)return;
      const res = await axiosInstance.get("/trays?employerId="+ selectedEmployerId);
      setTrayTypes(res.data.list || []);
    } catch {
      toast.error("Failed to load tray types");
    }
  };

  const openTrayPopup = (customer) => {
    setSelectedCustomerTrayId(customer._id);
    setTrayTypeId("");
    setTrayCount("");
    setOpenTrayDialog(true);
  };

  const handleUpdateTray = async () => {
    try {
      setSavingTray(true);

      await axiosInstance.put(
        `/customers/${selectedCustomerTrayId}/tray-balance`,
        {
          trays: [
            {
              trayTypeId: trayTypeId,
              count: Number(trayCount)
            }
          ]
        }
      );

      toast.success("Tray balance updated");
      setOpenTrayDialog(false);

    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update tray");
    } finally {
      setSavingTray(false);
    }
  };

  const getEmployersList = async () => {
    try {
      const res = await axiosInstance.get("/employers");
      setEmployers(res.data);
      if(res.data.length > 0){
        setSelectedEmployerId(res.data[0]['_id'])
      }
    } catch {
      console.log('Error')
    }
  }

  const getBranchList = async () => {
    try {
      const response = await axiosInstance.get('/branches?employerId='+ selectedEmployerId)

      setBranches(response?.data?.list)
      if(response?.data?.list?.length > 0){
        setFilteredBranch(response?.data?.list[0]?._id)
      }else{
        setFilteredBranch('')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error retrieving list')
    }
  }

  // Fetch Customers
  const getCustomersList = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/customers/list?branchId=${filteredBranch}&searchString=${searchString}&employerId=${selectedEmployerId}`
      );
      setCustomersList(response.data.result || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  // Close Popup
  const handleCreateCustomerPopupClose = () => {
    setOpen(false);
    setCustomerId('')
    reset({
      name: '',
      mobile: '',
      location: '',
      branch: ''
    })
    setEditCustomer(false)
  };

  // Submit New Customer
  const handleCreateCustomerSubmit = async (data) => {
    try {
      setSaving(true);
      let path = "/customers/create"
      const obj = {
        location: data?.location,
        mobile: data?.mobile,
        name: data?.name,
        branchId: data?.branch,
      }
      if(user?.roleId === 1){
        obj.employerId = selectedEmployerId
      }
      if(editCustomer){
        path = '/customers/update'
        obj.customerId = customerId
      }
      const response = await axiosInstance.post(path, obj);
      toast.success(response.data.message || "Customer saved successfully");
      handleCreateCustomerPopupClose();
      getCustomersList();
      setSaving(false);
      setEditCustomer(false)
    } catch (err) {
      setSaving(false);
      toast.error(err.response?.data?.message || "Failed to add customer");
    }
  };

  // Delete Customer
  const handleDeleteCustomer = async (customerId) => {
    setCustomerToDelete(customerId);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteCustomer = async () => {
    try {
      const response = await axiosInstance.delete(`/customers/${customerToDelete}`);
      toast.success(response.data.message || "Customer deleted successfully");
      getCustomersList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete customer");
    } finally {
      setOpenDeleteDialog(false);
      setCustomerToDelete(null);
    }
  };

  const cancelDeleteCustomer = () => {
    setOpenDeleteDialog(false);
    setCustomerToDelete(null);
  };

  const paginatedCustomers = customersList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Open Edit Customer Popup
  const openEditCustomerPopup = (customer) => {
    setEditCustomer(true)
    setCustomerId(customer?._id)
    reset({
      name: customer?.name,
      mobile: customer?.mobile,
      location: customer?.location,
      branch: customer?.branchId
    })
    setOpen(true)
  }

  const getBranchName = branchId => {
    return branches.find(branch => branch?._id === branchId )?.name
  }

  const getEmployerName = () => {
    return paginatedCustomers?.length > 0 && paginatedCustomers[0]?.employerDetails?.employerName
  }

  const printPDF = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
      <meta charset="UTF-8" />
      <title>Due Report - ${new Date().toLocaleDateString("en-IN")}</title>

      <style>
        body {
          font-family: Arial, Helvetica, sans-serif;
          margin: 4px;
          color: #000;
        }

        h2 {
          margin: 0;
          text-transform: capitalise;
        }

        .header {
          border-bottom: 2px solid #000;
          padding-bottom: 8px;
          margin-bottom: 12px;
        }

        .meta {
          font-size: 12px;
          margin-top: 4px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          margin-top: 12px;
        }

        th, td {
          border: 1px solid #000;
          padding: 6px;
          vertical-align: middle;
        }

        thead th {
          background: #eee;
          text-align: center;
          font-weight: bold;
        }

        .right {
          text-align: right;
        }

        tr {
          page-break-inside: avoid;
        }

        .footer {
          margin-top: 14px;
          font-size: 10px;
          border-top: 1px solid #000;
          padding-top: 6px;
          display: flex;
          justify-content: space-between;
        }

        @page {
          size: A5 portrait;   /* or A5 landscape */
          margin: 10mm;
        }
      </style>
      </head>

      <body>
        <div style="display: flex; align-items: center; gap: 12px">
          <img
            src=${LOGO_BASE64}
            alt="DBF Logo"
            style="width: 50px; height: 50px; border-radius: 4px"
          />
          <div>
            <div
              style="font-size: 20px; font-weight: bold; color: #1976d2;">
              ${getEmployerName()}
            </div>
            <div style="color: #666; font-size: 16px">
              ${getBranchName(filteredBranch)}
            </div>
          </div>
        </div>

      <div class="header">
        <h2>Customers Due Report</h2>
        <div class="meta">
          Generated on: ${new Date().toLocaleString("en-IN")}
        </div>
      </div>

      <table>
      <thead>
      <tr>
        <th style="width: 45%">Customer Name</th>
        <th style="width: 35%">Location</th>
        <th style="width: 20%">Due Amount (₹)</th>
      </tr>
      </thead>

      <tbody>
      ${customersList
        .map(
          (c) => `
      <tr>
        <td>${c.name || "-"}</td>
        <td>${c.location || "-"}</td>
        <td class="right">
          ${Number(c.totalDueAmount || 0).toLocaleString("en-IN")}
        </td>
      </tr>`
        )
        .join("")}
      </tbody>
      </table>

      <div class="footer">
        <span>Total Customers: ${customersList.length}</span>
        <span>Total Due Amount: ${customersList.reduce((acc, el)=>acc + el.totalDueAmount, 0).toLocaleString("en-IN")}</span>
      </div>

      </body>
      </html>
    `);
     printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Customers
        </Typography>
        <Box>
          <TextField
            sx={{ marginRight: "8px" }}
            placeholder="Search by Name/Mobile"
            name="search"
            size="small"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
          />

          {
            user?.roleId === 1 && 
            <TextField
              sx={{ marginRight: "8px" }}
              select
              label="Employer"
              size="small"
              value={selectedEmployerId}
              onChange={(e) => setSelectedEmployerId(e.target.value)}
            >
              {employers.map((employer) => (
                <MenuItem key={employer._id} value={employer._id}>
                  {employer.employerName}
                </MenuItem>
              ))}
            </TextField>
          }

          {
            branches.length > 0 && 
            <TextField
              sx={{ marginRight: "8px" }}
              select
              label="Branch"
              size="small"
              value={filteredBranch}
              onChange={(e) => setFilteredBranch(e.target.value)}
            >
              {branches.map((branch) => (
                <MenuItem key={branch._id} value={branch._id}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
          }
          {
            user?.roleId !== 1 &&
            <Button
              sx={{ marginRight: "8px" }}
              variant="outlined"
              onClick={printPDF}
              disabled={customersList.length == 0}
            >
              Print PDF
            </Button>
          }
          <Button onClick={() => setOpen(true)} variant="contained">
            Add Customer
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" my={4}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading customers...
          </Typography>
        </Box>
      ) : (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <b>Name</b>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <b>Mobile No.</b>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <b>Location</b>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <b>Total Amount Due</b>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCustomers.length > 0 ? (
                  paginatedCustomers.map((customer) => (
                    <TableRow hover key={customer._id}>
                      <TableCell
                        sx={{
                          py: 1,
                        }}
                      >
                        {customer.name}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", py: 1 }}>
                        +91 {customer.mobile}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                        }}
                      >
                        {customer.location}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                        }}
                      >
                        {`₹ ${Number(
                          customer.totalDueAmount || 0
                        ).toLocaleString("en-IN")}`}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                        }}
                      >
                        {
                          user?.roleId !== 1 &&
                          <Tooltip title="View Details" arrow placement="top">
                            <IconButton
                              color="primary"
                              onClick={() =>
                                navigate(`/customer-details/${customer._id}`)
                              }
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        }
                        {
                          [3].indexOf(user?.roleId) <= -1 && <Tooltip title="Edit" arrow placement="top">
                          <IconButton
                            color="primary"
                            onClick={()=>openEditCustomerPopup(customer)}
                          >
                            <FaEdit size={19} />
                          </IconButton>
                        </Tooltip>
                        }
                        {
                          user?.roleId === 1 && (
                            <Tooltip title="Update Tray Balance">
                              <IconButton
                                color="secondary"
                                onClick={() => openTrayPopup(customer)}
                              >
                                <Inventory2Icon />
                              </IconButton>
                            </Tooltip>
                          )
                        }
                        {
                          [3].indexOf(user?.roleId) <= -1 && (
                            <Tooltip title="Delete Customer" arrow placement="top">
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteCustomer(customer._id)}
                              >
                                <FaTrash size={19} />
                              </IconButton>
                            </Tooltip>
                          )
                        }
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ py: 3, color: "text.secondary" }}
                    >
                      <Typography
                        color="text.secondary"
                        variant="h6"
                        fontWeight="bold"
                      >
                        No customers found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={customersList.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        </Paper>
      )}

      {/* Dialog for Add Customer */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleCreateCustomerPopupClose();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          variant="h6"
          fontWeight="bold"
          sx={{
            background: "#f5f5f5",
          }}
        >
          {editCustomer ? 'Edit Customer' : 'Add Customer'}
        </DialogTitle>
        <form onSubmit={handleSubmit(handleCreateCustomerSubmit)} noValidate>
          <DialogContent>
            <Box display="grid" gap={2} mt={1}>
              <TextField
                label="Name"
                name="name"
                fullWidth
                required
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
              />

              <TextField
                label="Mobile Number"
                name="mobile"
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">🇮🇳 +91</InputAdornment>
                  ),
                  inputMode: "numeric",
                }}
                {...register("mobile")}
                error={!!errors.mobile}
                helperText={errors.mobile?.message}
              />

              <TextField
                label="Location"
                name="location"
                fullWidth
                required
                {...register("location")}
                error={!!errors.location}
                helperText={errors.location?.message}
              />

              <FormControl
                sx={{ flex: 1 }}
                error={!!errors?.branch}
              >
                <InputLabel>Branch</InputLabel>

                <Controller
                  name={`branch`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Branch"
                      value={field.value ?? ""}
                      disabled={editCustomer}
                      onChange={(e) =>
                        field.onChange(e.target.value)
                      }
                    >
                      {branches.map((branch) => (
                        <MenuItem key={branch._id} value={branch._id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />

                <FormHelperText>
                  {errors?.branch?.message}
                </FormHelperText>
              </FormControl>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCreateCustomerPopupClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Submitting" : "Submit"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={openTrayDialog}
        onClose={() => setOpenTrayDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ background: "#f5f5f5", fontWeight: "bold" }}
        >
          Update Tray Balance
        </DialogTitle>

        <DialogContent>
          <Box display="grid" gap={2} mt={1}>

            <FormControl fullWidth>
              <InputLabel>Tray Type</InputLabel>
              <Select
                value={trayTypeId || ""}
                label="Tray Type"
                onChange={(e) => setTrayTypeId(Number(e.target.value))}
              >
                {trayTypes.map((tray) => (
                    tray?.status && 
                    <MenuItem key={tray.trayTypeId} value={tray.trayTypeId}>
                      {tray.name + '(₹'+tray.price+')'}
                    </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Tray Count"
              type="number"
              fullWidth
              value={trayCount}
              onChange={(e) => setTrayCount(e.target.value)}
            />

          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenTrayDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleUpdateTray}
            disabled={savingTray}
          >
            {savingTray ? "Saving..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={openDeleteDialog}
        onClose={cancelDeleteCustomer}
        onConfirm={confirmDeleteCustomer}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
}

export default ListPage;